<template>
  <div class="tbi-entry">
    <div
      v-for="item in tabs"
      :key="item.key"
      class="tbi-btn"
      :class="{ 'has-msg': item.count > 0 }"
      @click="goWorkOrder"
    >
      <span class="tbi-label">{{ item.label }}</span>
      <transition name="tbi-pop">
        <span v-if="item.count > 0" class="tbi-badge">
          <span class="tbi-badge-inner">{{ formatCount(item.count) }}</span>
        </span>
      </transition>
    </div>
    <!-- 手动刷新：请求中（含轮询）icon 旋转 -->
    <div
      class="tbi-refresh"
      :class="{ 'is-fetching': fetching }"
      title="刷新待办数量"
      @click="fetchTodoCounts"
    >
      <i class="el-icon-refresh" />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'

// 外部工单系统地址 / 待办数量接口：由各环境 env 文件注入（.env.development / .env.test / .env.production）
const WORK_ORDER_URL = process.env.VUE_APP_WORK_ORDER_URL
const TODO_COUNT_API = process.env.VUE_APP_TODO_COUNT_API
// 待办数量轮询间隔（ms）
const POLL_INTERVAL = 60 * 1000

export default {
  name: 'TbiEntry',
  data() {
    return {
      // field 为接口返回字段名：待指派/待回复/待处理
      tabs: [
        { key: 'assign', label: '待指派', count: 0, field: 'pendingAssign' },
        { key: 'reply', label: '待回复', count: 0, field: 'pendingReply' },
        { key: 'process', label: '待处理', count: 0, field: 'myPending' }
      ],
      pollTimer: null,
      fetching: false
    }
  },
  computed: {
    ...mapGetters(['token', 'name'])
  },
  watch: {
    // 登录帐号名就绪后立即拉取一次
    name: {
      immediate: true,
      handler(val) {
        if (val) this.fetchTodoCounts()
      }
    }
  },
  mounted() {
    // 定时轮询，让角标能实时感知新消息
    this.pollTimer = setInterval(this.fetchTodoCounts, POLL_INTERVAL)
  },
  beforeDestroy() {
    clearInterval(this.pollTimer)
  },
  methods: {
    formatCount(count) {
      return count > 99 ? '99+' : String(count)
    },
    // 拉取外部工单系统真实待办数量
    async fetchTodoCounts() {
      if (!this.name || this.fetching) return
      this.fetching = true
      try {
        const { data: res } = await axios.get(TODO_COUNT_API, {
          params: {
            username: this.name,
            // 追加 5 位随机数防止接口缓存
            _t: Math.random().toString(36).slice(2, 7)
          }
        })
        // 异常场景（如"用户不存在"）code 仍为 200 但 data 为 null，此时不更新角标
        if (res?.code === 200 && res.data) {
          this.tabs.forEach((tab) => {
            tab.count = Number(res.data[tab.field]) || 0
          })
        }
      } catch (e) {
        // 接口不可用时保持当前数量，无需打扰用户
      } finally {
        this.fetching = false
      }
    },
    // 跳转外部工单系统，地址栏携带当前用户 token 供外部系统认证
    goWorkOrder() {
      let url = WORK_ORDER_URL
      if (this.token) {
        url = url.replace('#', `?token=${encodeURIComponent(this.token)}#`)
      }
      window.open(url, '_blank')
    }
  }
}
</script>

<style lang="scss" scoped>
// 毛玻璃容器：半透明渐变底 + 背景模糊 + 内侧高光，营造通透质感
.tbi-entry {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 6px 4px;
  margin-right: 14px;
  vertical-align: middle;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.72),
    rgba(255, 255, 255, 0.36)
  );
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 10px;
  backdrop-filter: blur(12px) saturate(1.5);
  -webkit-backdrop-filter: blur(12px) saturate(1.5);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9),
    0 2px 8px rgba(31, 45, 61, 0.06);
}

.tbi-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 12px;
  color: #4e5969;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.25s ease, box-shadow 0.25s ease,
    transform 0.15s ease;

  .tbi-label {
    line-height: 1;
    transition: color 0.25s ease;
  }

  // 悬浮：白色玻璃小片 + 微投影
  &:hover {
    background: rgba(255, 255, 255, 0.85);
    box-shadow: 0 1px 6px rgba(31, 45, 61, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.9);

    .tbi-label {
      color: #409eff;
    }
  }

  &:active {
    transform: scale(0.95);
  }

  // 有新消息：淡蓝玻璃高亮
  &.has-msg {
    background: rgba(64, 158, 255, 0.12);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);

    .tbi-label {
      color: #409eff;
      font-weight: 600;
    }
  }
}

// 刷新按钮：与三个按钮同处玻璃容器，前置细分隔线增加结构感
.tbi-refresh {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-left: 4px;
  border-radius: 6px;
  font-size: 13px;
  color: #909399;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.25s ease, color 0.25s ease;

  // 与按钮组之间的竖向发丝线
  &::before {
    content: '';
    position: absolute;
    left: -5px;
    top: 50%;
    width: 1px;
    height: 12px;
    background: rgba(148, 163, 184, 0.3);
    transform: translateY(-50%);
  }

  &:hover {
    background: rgba(255, 255, 255, 0.85);
    color: #409eff;
    box-shadow: 0 1px 6px rgba(31, 45, 61, 0.1);
  }

  // 请求中（手动刷新 / 轮询）：旋转 loading
  &.is-fetching {
    color: #409eff;
    pointer-events: none;

    .el-icon-refresh {
      animation: tbi-rotate 0.8s linear infinite;
    }
  }
}

@keyframes tbi-rotate {
  to {
    transform: rotate(360deg);
  }
}

// 消息数字角标：锚定按钮右上角，收在容器内，不再越界压到相邻按钮
.tbi-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  display: block;

  .tbi-badge-inner {
    display: block;
    box-sizing: border-box;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    border-radius: 9px;
    background: #f56c6c;
    // 白色描边环 + 顶部内高光，玻璃质感角标
    border: 1px solid rgba(255, 255, 255, 0.9);
    color: #fff;
    font-size: 10px;
    font-weight: 600;
    line-height: 14px;
    text-align: center;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.35),
      0 2px 4px rgba(245, 108, 108, 0.4);
    // 微信新消息抖动动效：抖一下、歇一会，循环提醒且不吵
    animation: tbi-shake 2.6s ease-in-out infinite;
  }
}

@keyframes tbi-shake {
  0%,
  40%,
  100% {
    transform: rotate(0deg) scale(1);
  }

  5% {
    transform: rotate(-16deg) scale(1.15);
  }

  10% {
    transform: rotate(14deg) scale(1.15);
  }

  15% {
    transform: rotate(-10deg) scale(1.08);
  }

  20% {
    transform: rotate(8deg) scale(1.04);
  }

  25% {
    transform: rotate(-5deg) scale(1);
  }

  30% {
    transform: rotate(3deg);
  }

  35% {
    transform: rotate(-1deg);
  }
}

// 角标出现 / 消失的弹性动效
.tbi-pop-enter-active {
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1),
    opacity 0.2s ease;
}

.tbi-pop-leave-active {
  transition: transform 0.15s ease-in, opacity 0.15s ease-in;
}

.tbi-pop-enter,
.tbi-pop-leave-to {
  opacity: 0;
  transform: scale(0);
}
</style>
